import { useOnboardingStore, UploadedMethodType } from '../../../store/onboardingStore';
import { Toggle } from '../ui/Toggle';
import { useState, useEffect, useMemo } from 'react';
import {
  Gift,
  ShoppingBag
} from 'lucide-react';
import SearchPreviousProducts from '../../onboarding/components/SearchPreviousProducts';
import { ProductLinkInput } from '../ui/ProductLinkInput';
import { ProductManualInputs } from '../ui/ProductManualInputs';
import { dictionary } from '../../../utils/dictionary';
import { useAuth } from '../../../context/AuthContext';
import { useTutorialContext } from '../../../context/TutorialContext';
import { useGenericImageUpload } from '../../../hooks/useGenericImageUpload';
import { toBase64 } from '../../../utils/utils';
import { uploadImagesToS3 } from '../../../services/s3/s3Service';
import toast from 'react-hot-toast';
import { FOLDER_IMAGES, MAX_FILE_SIZE } from '../../onboarding/const/products';

export type UpsellProduct = {
  id: string;
  productName: string;
  price: string;
  description: string;
  image?: string;
  url_images?: string[];
  isManualEntry: boolean;
  productLink: string;
  externalId?: string | null;
  providerId?: string | null;
  isLoading?: boolean;
  error?: string;
  uploadedMethod?: UploadedMethodType;
};

export function StepAdditionalOffers() {
  const {
    data,
    updateAdditionalOffers,
    previousProducts,
    isPreviousProductsLoading,
    fetchPreviousProducts
  } = useOnboardingStore();
  const { additionalOffers } = data;
  const { language } = useAuth();
  const { setCurrentStepIndex, resumeTutorial, steps } = useTutorialContext();
  const [upsells, setUpsells] = useState<UpsellProduct[]>(
    additionalOffers.upsell?.enabled === true && additionalOffers.upsell?.products
      ? additionalOffers.upsell.products
      : [{
        id: Date.now().toString(),
        productName: '',
        price: '0.00',
        description: '',
        image: undefined,
        url_images: undefined,
        isManualEntry: false,
        productLink: '',
        externalId: null,
        providerId: null,
        isLoading: false,
        error: ''
      }]
  );

  const [bumpOffer, setBumpOffer] = useState({
    enabled: additionalOffers.bumpOffer?.enabled ?? false,
    productName: additionalOffers.bumpOffer?.productName || '',
    price: additionalOffers.bumpOffer?.price || '0.00',
    description: additionalOffers.bumpOffer?.description || '',
    image: additionalOffers.bumpOffer?.image || undefined,
    isManualEntry: additionalOffers.bumpOffer?.isManualEntry ?? false,
    productLink: additionalOffers.bumpOffer?.productLink || '',
    externalId: additionalOffers.bumpOffer?.externalId || null,
    providerId: additionalOffers.bumpOffer?.providerId || null,
    url_images: additionalOffers.bumpOffer?.url_images || undefined,
    isLoading: false,
    error: '',
    uploadedMethod: additionalOffers.bumpOffer?.uploadedMethod || UploadedMethodType.NONE
  });

  const [currentEditingUpsell, setCurrentEditingUpsell] = useState<string | null>(
    upsells.length > 0 ? upsells[0].id : null
  );

  const [upsellToggleEnabled, setUpsellToggleEnabled] = useState<boolean>(additionalOffers.upsell?.enabled ?? false);

  const [bumpOfferMethod, setBumpOfferMethod] = useState<'link' | 'manual' | 'previous' | null>(null);
  const [upsellMethods, setUpsellMethods] = useState<Map<string, 'link' | 'manual' | 'previous' | null>>(new Map());
  const [upsellImageUploadStates, setUpsellImageUploadStates] = useState<Map<string, { isLoading: boolean }>>(new Map());

  useEffect(() => {
    if (upsellToggleEnabled === false) {
      setUpsells([]);
      updateAdditionalOffers({
        ...additionalOffers,
        upsell: {
          enabled: false,
          products: []
        }
      });
    }
  }, [upsellToggleEnabled]);

  useEffect(() => {
    if (!additionalOffers.upsell) {
      updateAdditionalOffers({
        upsell: {
          enabled: true,
          products: upsells
        },
        bumpOffer: {
          enabled: false,
          productName: '',
          price: '0.00',
          description: '',
          image: undefined,
          url_images: undefined,
          isManualEntry: false,
          productLink: '',
        }
      });
    }
  }, []);

  useEffect(() => {
    if (additionalOffers.upsell?.products) {
      setUpsells(additionalOffers.upsell.products);
    }
    if (additionalOffers.bumpOffer) {
      setBumpOffer((prev: any) => ({
        ...prev,
        ...additionalOffers.bumpOffer
      }));
    }
  }, [additionalOffers]);

  useEffect(() => {
    fetchPreviousProducts();
  }, []);

  const handleUpsellToggle = (enabled: boolean) => {
    setUpsellToggleEnabled(enabled);
    if (enabled) {
      handleAddUpsell();
    }
  };

  const handleBumpOfferToggle = (enabled: boolean) => {
    if (enabled === false) {
      setBumpOfferMethod(null);
      updateAdditionalOffers({
        ...additionalOffers,
        bumpOffer: {
          ...bumpOffer,
          enabled: false,
          productName: '',
          price: '0.00',
          description: '',
          image: undefined,
          url_images: undefined,
          isManualEntry: false,
          productLink: '',
          uploadedMethod: UploadedMethodType.NONE
        }
      });
      return;
    }

    setBumpOffer({
      ...bumpOffer,
      enabled
    });

    updateAdditionalOffers({
      ...additionalOffers,
      bumpOffer: {
        ...bumpOffer,
        enabled
      }
    });
  };

  const bumpOfferCurrentImages = useMemo(() => {
    return bumpOffer.url_images ? [bumpOffer.url_images as string] : [];
  }, [bumpOffer.url_images]);

  const bumpOfferImageUpload = useGenericImageUpload({
    folder: FOLDER_IMAGES,
    currentImages: bumpOfferCurrentImages,
    onImagesUpdate: (images) => {
      const imageUrl = images[0] || undefined;
      setBumpOffer(prev => {
        const updated = {
          ...prev,
          image: undefined,
          uploadedMethod: UploadedMethodType.MANUAL,
          url_images: imageUrl
        };
        updateAdditionalOffers({
          ...additionalOffers,
          bumpOffer: updated
        });
        return updated;
      });
    },
    allowMultiple: false,
    maxImages: 1,
    maxFileSize: MAX_FILE_SIZE
  });

  const handleImageSelect = async (file: File, forUpsellId?: string) => {
    if (forUpsellId) {
      setUpsellImageUploadStates(prev => new Map(prev.set(forUpsellId, { isLoading: true })));
      
      try {
        const base64File = await toBase64(file);
        const filename = `${Date.now()}-${file.name}`;
        const response = await uploadImagesToS3({
          file: base64File,
          folder: FOLDER_IMAGES,
          filename: filename
        });

        const imageUrl = [response.s3_url];
        setUpsells(prevUpsells => {
          const updatedUpsells = prevUpsells.map(upsell =>
            upsell.id === forUpsellId 
              ? { 
                  ...upsell, 
                  image: undefined, 
                  uploadedMethod: UploadedMethodType.MANUAL, 
                  url_images: imageUrl 
                } 
              : upsell
          );

          updateAdditionalOffers({
            ...additionalOffers,
            upsell: {
              enabled: upsellToggleEnabled,
              products: updatedUpsells,
            }
          });

          return updatedUpsells;
        });

        toast.success(dictionary?.["success-uploading-images"]?.[language] || "Imagen subida exitosamente");
      } catch (error) {
        console.error('Error al subir la imagen:', error);
        toast.error(dictionary?.["error-uploading-images-too-long"]?.[language] || "Error al subir la imagen");
      } finally {
        setUpsellImageUploadStates(prev => new Map(prev.set(forUpsellId, { isLoading: false })));
      }
    } else {
      const fileList = [file] as any;
      const syntheticEvent = {
        target: { files: fileList }
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      bumpOfferImageUpload.handleImageUpload(syntheticEvent);
    }
  };

  const handleAddUpsell = () => {
    if (upsells.length < 1) {
      const newUpsell = {
        id: Date.now().toString(),
        productName: '',
        price: '0.00',
        description: '',
        image: undefined,
        isManualEntry: false,
        productLink: '',
        externalId: null,
        providerId: null,
        isLoading: false,
        error: ''
      };
      setUpsells([...upsells, newUpsell]);
      setCurrentEditingUpsell(newUpsell.id);
      updateAdditionalOffers({
        ...additionalOffers,
        upsell: {
          enabled: true,
          products: [...upsells, newUpsell]
        }
      });
    }
  };

  const handleUpsellChange = async (id: string, field: keyof UpsellProduct, value: any) => {
    const updatedUpsells = upsells.map(upsell =>
      upsell.id === id ? { ...upsell, [field]: value } : upsell
    );

    setUpsells(updatedUpsells);

    updateAdditionalOffers({
      ...additionalOffers,
      upsell: {
        enabled: true,
        products: updatedUpsells
      }
    });
  };

  const handleBumpOfferChange = async (field: string, value: any) => {
    const updatedBumpOffer = {
      ...bumpOffer,
      [field]: value,
      error: ''
    };

    setBumpOffer(updatedBumpOffer);

    updateAdditionalOffers({
      ...additionalOffers,
      bumpOffer: {
        enabled: updatedBumpOffer.enabled,
        productName: updatedBumpOffer.productName,
        price: updatedBumpOffer.price,
        description: updatedBumpOffer.description,
        image: updatedBumpOffer.image,
        url_images: updatedBumpOffer.url_images,
        isManualEntry: updatedBumpOffer.isManualEntry,
        productLink: updatedBumpOffer.productLink,
        uploadedMethod: updatedBumpOffer.uploadedMethod
      }
    });
  };

  const handlePreviousBumpOfferSelect = (product: any) => {
    if (!product) return;
    const updatedBumpOffer = {
      ...bumpOffer,
      enabled: true,
      productName: product.name,
      price: product.price.toString(),
      description: product.description,
      isManualEntry: false,
      image: '',
      url_images: product.images?.[0] || undefined,
      productLink: '',
      externalId: product.id,
      providerId: 'INTERNAL_FLUXI',
      uploadedMethod: UploadedMethodType.PREVIOUS
    };

    setBumpOffer(updatedBumpOffer);

    updateAdditionalOffers({
      ...additionalOffers,
      bumpOffer: updatedBumpOffer
    });
  };

  const handlePreviousUpsellSelect = (product: any, upsellId: string) => {
    if (!product) return;

    const updatedUpsells = upsells.map(upsell =>
      upsell.id === upsellId ? {
        ...upsell,
        productName: product.name,
        price: product.price.toString(),
        description: product.description,
        isManualEntry: false,
        image: '',
        url_images: product.images && product.images.length > 0 ? [product.images[0]] : undefined,
        productLink: '',
        externalId: product.id,
        providerId: 'INTERNAL_FLUXI',
        uploadedMethod: UploadedMethodType.PREVIOUS
      } : upsell
    );

    setUpsells(updatedUpsells);

    updateAdditionalOffers({
      ...additionalOffers,
      upsell: {
        enabled: true,
        products: updatedUpsells
      }
    });

    setCurrentEditingUpsell(upsellId);
  };

  const renderSelector = (handleMethodSelect: (method: 'link' | 'manual' | 'previous') => void, currentMethod: 'link' | 'manual' | 'previous' | null | undefined) => {
    return (
      <div className="flex gap-2 justify-center mt-6">
        <div className="flex gap-3 justify-center mb-6">
          <button
            onClick={() => handleMethodSelect('link')}
            className={`px-5 py-2 rounded-full font-medium transition-all border-2 flex items-center gap-2 ${currentMethod === 'link'
              ? 'bg-[#2B3674] text-white border-[#2B3674]'
              : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
              }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            {dictionary["link"][language]}
          </button>
          <button
            onClick={() => handleMethodSelect('manual')}
            className={`px-5 py-2 rounded-full font-medium transition-all border-2 flex items-center gap-2 ${currentMethod === 'manual'
              ? 'bg-[#2B3674] text-white border-[#2B3674]'
              : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
              }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            {dictionary["upload-photo"][language]}
          </button>
          <button
            onClick={() => handleMethodSelect('previous')}
            className={`px-5 py-2 rounded-full font-medium transition-all border-2 flex items-center gap-2 ${currentMethod === 'previous'
              ? 'bg-[#2B3674] text-white border-[#2B3674]'
              : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
              }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {dictionary["your-products"][language]}
          </button>
        </div>
      </div>
    )

  }

  const renderMethodSelector = (type: 'bump' | 'upsell', upsellId?: string) => {
    const currentMethod = type === 'bump' ? bumpOfferMethod : upsellMethods.get(upsellId || '');

    const handleMethodSelect = (method: 'link' | 'manual' | 'previous') => {
      if (type === 'bump') {
        setBumpOfferMethod(method);
        if (method === 'link') {
          handleBumpOfferChange('uploadedMethod', UploadedMethodType.LINK);
        } else if (method === 'manual') {
          handleBumpOfferChange('uploadedMethod', UploadedMethodType.MANUAL);
        } else if (method === 'previous') {
          handleBumpOfferChange('uploadedMethod', UploadedMethodType.PREVIOUS);
        }
      } else if (upsellId) {
        setUpsellMethods(new Map(upsellMethods.set(upsellId, method)));
        if (method === 'link') {
          handleUpsellChange(upsellId, 'uploadedMethod', UploadedMethodType.LINK);
        } else if (method === 'manual') {
          handleUpsellChange(upsellId, 'uploadedMethod', UploadedMethodType.MANUAL);
        } else if (method === 'previous') {
          handleUpsellChange(upsellId, 'uploadedMethod', UploadedMethodType.PREVIOUS);
        }
      }
    };

    if (!currentMethod) {
      return (
        renderSelector(handleMethodSelect, currentMethod)
      );
    }

    if (currentMethod === 'link') {
      return (
        <div className="mt-3">
          {renderSelector(handleMethodSelect, currentMethod)}
          <div className="relative mt-3">
            <ProductLinkInput
              value={type === 'bump' ? bumpOffer.productLink : upsells.find(u => u.id === upsellId)?.productLink || ''}
              placeholder={dictionary["paste-product-url-placeholder"][language]}
              onChange={(value) => {
                if (type === 'bump') {
                  handleBumpOfferChange('productLink', value);
                } else if (upsellId) {
                  handleUpsellChange(upsellId, 'productLink', value);
                }
              }}
            />
          </div>
        </div>
      );
    }

    if (currentMethod === "manual") {
      type ManualProduct = {
        image?: string;
        productName?: string;
        price?: string | number;
        description?: string;
        url_images?: string | string[];
      };

      const current: ManualProduct =
        type === "bump"
          ? bumpOffer
          : (upsells.find((u) => u.id === upsellId) as ManualProduct) || {};

      const currentImage = type === "bump" 
        ? (current.url_images ? [current.url_images as string] : [])
        : (current.url_images && Array.isArray(current.url_images) ? current.url_images : (current.url_images ? [current.url_images as string] : []));

      const manualData = {
        images: currentImage,
        name: current.productName || "",
        price: current.price || "",
        description: current.description || "",
      };

      const handleManualDataChange = (field: string, value: string) => {
        const map: Record<string, string> = {
          name: "productName",
          price: "price",
          description: "description",
        };

        if (type === "bump") {
          handleBumpOfferChange(map[field], value);
        } else if (upsellId) {
          handleUpsellChange(upsellId, map[field] as keyof UpsellProduct, value);
        }
      };

      const handleImageUploadWrapper = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
          handleImageSelect(file, type === "bump" ? undefined : upsellId);
        }
      };

      const handleRemoveImageWrapper = (_index: number) => {
        if (type === "bump") {
          bumpOfferImageUpload.removeImage(0);
        } else if (upsellId) {
          handleUpsellChange(upsellId, "url_images", undefined);
          handleUpsellChange(upsellId, "image", undefined);
        }
      };

      const isUploading = type === "bump" 
        ? bumpOfferImageUpload.isLoading
        : (upsellId ? upsellImageUploadStates.get(upsellId)?.isLoading || false : false);

      return (
        <div className="mt-3 space-y-3">
          {renderSelector(handleMethodSelect, currentMethod)}

          <ProductManualInputs
            manualData={manualData}
            handleImageUpload={handleImageUploadWrapper}
            handleRemoveImage={handleRemoveImageWrapper}
            handleManualDataChange={handleManualDataChange}
            isUploadingImages={isUploading}
          />
        </div>
      );
    }

    if (currentMethod === 'previous') {
      const handlePreviousProductSelectWrapper = (e: React.ChangeEvent<HTMLInputElement>) => {
        const productId = e.target.value;
        const product = previousProducts.find(p => p.id === productId);
        if (product) {
          if (type === 'bump') {
            handlePreviousBumpOfferSelect(product);
          } else if (upsellId) {
            handlePreviousUpsellSelect(product, upsellId);
          }
        }
      };

      return (
        <div className="mt-3 space-y-3">
          {renderSelector(handleMethodSelect, currentMethod)}
          <SearchPreviousProducts
            previousProducts={previousProducts}
            handlePreviousProductSelect={handlePreviousProductSelectWrapper}
            isPreviousProductsLoading={isPreviousProductsLoading}
          />
        </div>
      );
    }

    return null;
  };

  const handleOrderBump = () => {
    setCurrentStepIndex(2);
    resumeTutorial();
  };

  const handleUpsell = () => {
    setCurrentStepIndex(3);
    resumeTutorial();
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {dictionary["increase-your-revenue-with-complementary-products"][language]}
        </h1>
      </div>

      <div className="max-w-3xl mx-auto space-y-4">
        <div id="orderBump-target" className={`${bumpOffer.enabled ? 'bg-[#F6FFFA] border-[#E3FBEB] solid border-2' : 'bg-[#E3FBEB]'} from-green-50 to-emerald-50 rounded-[25px] px-6 py-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center bg-white">
                <Gift className="w-7 h-7 text-green-800" />
              </div>
              <div>
                <h3 className="text-md font-bold text-gray-900">
                  {dictionary["add-order-bump"][language]}
                </h3>
                <p onClick={() => handleOrderBump()} className="text-xs text-[#62748E] hover:text-blue-500 hover:underline transition-colors cursor-pointer">
                  {dictionary["what-is-an-order-bump"][language]}
                </p>
              </div>
            </div>
            <Toggle enabled={bumpOffer.enabled} onChange={handleBumpOfferToggle} />
          </div>

          <div
            className={`
              overflow-hidden
              transition-all duration-300
              ${bumpOffer.enabled
                ? "opacity-100 max-h-[500px]"
                : "opacity-0 max-h-0"
              }
            `}
          >
            {renderMethodSelector("bump")}
          </div>
        </div>

        <div id="upsell-target" className={`${upsellToggleEnabled ? 'bg-[#FBFAFF] border-[#EFECFF] solid border-2' : 'bg-[#EFECFF]'} from-purple-50 to-pink-50 rounded-[25px] px-6 py-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-2xl flex items-center justify-center bg-white">
                <ShoppingBag className="w-7 h-7 text-[#6D29D9]" />
              </div>
              <div>
                <h3 className="text-md font-bold text-gray-900">
                  {dictionary["add-upsell"][language]}
                </h3>
                <p onClick={() => handleUpsell()} className="text-xs text-[#62748E] hover:text-blue-500 hover:underline transition-colors cursor-pointer">
                  {dictionary["what-is-an-upsell"][language]}
                </p>
              </div>
            </div>
            <Toggle enabled={upsellToggleEnabled} onChange={handleUpsellToggle} />
          </div>

          <div
            className={`
                  overflow-hidden
                  transition-all duration-300
                  ${upsellToggleEnabled
                ? "opacity-100 max-h-[500px]"
                : "opacity-0 max-h-0"
              }
                `}
          >
            {renderMethodSelector('upsell', upsells[0]?.id || '')}
          </div>
        </div>
      </div>
    </div>
  );
}
